/**
 * Ma Petite Yole – Push notification helpers (browser API + service worker).
 * We use foreground/visible-tab notifications powered by the existing polling loop.
 * This works on desktop and installed PWAs and does not require a push server.
 */

const STORAGE_KEY = "yoles_push_optin";
const SEEN_KEY = "yoles_push_seen_ids";

export const pushSupported = () =>
  typeof window !== "undefined" &&
  "Notification" in window &&
  "serviceWorker" in navigator;

export const pushPermission = () => (pushSupported() ? Notification.permission : "denied");

export const isPushEnabled = () => {
  if (!pushSupported()) return false;
  return pushPermission() === "granted" && localStorage.getItem(STORAGE_KEY) === "1";
};

export async function registerServiceWorker() {
  if (!pushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.register("/sw.js");
    return reg;
  } catch (e) {
    console.warn("Service worker registration failed:", e);
    return null;
  }
}

export async function enablePush() {
  if (!pushSupported()) {
    return { ok: false, error: "Ce navigateur ne supporte pas les notifications." };
  }
  const perm = Notification.permission === "granted"
    ? "granted"
    : await Notification.requestPermission();
  if (perm !== "granted") {
    localStorage.setItem(STORAGE_KEY, "0");
    return { ok: false, error: perm === "denied" ? "Notifications bloquées dans le navigateur." : "Permission refusée." };
  }
  await registerServiceWorker();
  localStorage.setItem(STORAGE_KEY, "1");
  return { ok: true };
}

export function disablePush() {
  localStorage.setItem(STORAGE_KEY, "0");
}

/** Seen-ids helpers so a notification never fires twice on the same event. */
function getSeenIds() {
  try {
    return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]"));
  } catch {
    return new Set();
  }
}

function saveSeenIds(set) {
  const arr = Array.from(set).slice(-200); // cap size
  localStorage.setItem(SEEN_KEY, JSON.stringify(arr));
}

/** Prime the seen set with the currently-known ids (call after login). */
export function primeSeenIds(ids) {
  const set = new Set(ids);
  saveSeenIds(set);
}

/**
 * Fire OS notifications for any new items since last poll.
 * `items` should be the full recent notifications list (any order).
 * Returns the ids that were shown.
 */
export async function fireNewNotifications(items) {
  if (!isPushEnabled() || !items?.length) return [];
  const seen = getSeenIds();
  // Only fire for items newer than the last minute to avoid a burst on first login.
  const cutoff = Date.now() - 60 * 60 * 1000; // ignore anything > 1h old
  const fresh = items.filter((n) => {
    if (seen.has(n.id)) return false;
    const t = Date.parse(n.created_at);
    return !isNaN(t) && t >= cutoff;
  });

  const fired = [];
  for (const n of fresh.reverse()) {
    // Reverse so oldest fires first – on Chrome, latest ends up on top.
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const opts = {
        body: n.message || "",
        tag: n.id,
        data: { url: "/notifications" },
        badge: "/favicon.ico",
        icon: "/favicon.ico",
        // renotify only when supported
      };
      if (reg && "showNotification" in reg) {
        await reg.showNotification(n.title || "Ma Petite Yole", opts);
      } else {
        new Notification(n.title || "Ma Petite Yole", opts);
      }
      fired.push(n.id);
    } catch (e) {
      // ignore permission or platform errors
    }
    seen.add(n.id);
  }
  // Also add non-fresh items to seen so they never fire later.
  for (const n of items) seen.add(n.id);
  saveSeenIds(seen);
  return fired;
}
